import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, Close } from '@mui/icons-material';
import { useMutation } from '@apollo/client';
import { ShopifyInventoryItem } from '../../../graphql/generated';
import { CREATE_LISTINGS } from '../../../graphql/mutations';
import { GET_LISTINGS } from '../../../graphql/queries';

interface CreateListingsModalProps {
  open: boolean;
  onClose: () => void;
  selectedProducts: ShopifyInventoryItem[];
}

const CreateListingsModal: React.FC<CreateListingsModalProps> = ({ open, onClose, selectedProducts }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState<{ [key: string]: boolean }>({});

  const [createListings] = useMutation(CREATE_LISTINGS, {
    refetchQueries: [
      { query: GET_LISTINGS },
    ],
  });
  

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCheckboxChange = (variantId: string) => {
    setSelectedVariants((prevSelected) => ({
      ...prevSelected,
      [variantId]: !prevSelected[variantId],
    }));
  };

const handleCreateListings = async () => {
    const listings = selectedProducts.map((product) => {
        // Filter selected variants
        const filteredVariants = product.variants.filter(variant => selectedVariants[variant.id]);

        return {
            shopifyProductId: product.id,
            title: product.title,
            vendor: product.vendor || '',
            product_type: product.product_type || '',
            body_html: product.body_html || '',
            handle: product.handle || '',
            status: product.status || '',
            variants: filteredVariants.map(variant => ({
                id: variant.id,
                title: variant.title,
                sku: variant.sku || '',
                price: variant.price || 100,  // Default price if not provided
                inventoryQuantity: variant.inventoryQuantity || 0,
                images: variant.images
            })),
            images: product.images.map(image => ({
                id: '',
                src: image.src,
                altText: image.altText || '',
                position: image.position,
            })),
            options: product.options.map(option => ({
                id: option.id ?? '',
                name: option.name,
                position: option.position,
                values: option.values,
            })),
        };
    });

    try {
        await createListings({
            variables: {
                listings,
            },
        });
        onClose();
    } catch (error) {
        console.error('Error creating listings:', error);
    }
};



  if (selectedProducts.length === 0) {
    return null;
  }

  const currentProduct = selectedProducts[activeStep];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create Listings
        <Button onClick={onClose} style={{ position: 'absolute', right: 10, top: 10 }}>
          <Close />
        </Button>
      </DialogTitle>
      <DialogContent>
        <Box my={2}>
          <Typography variant="h6">{currentProduct.title}</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                {Array.from(new Set(currentProduct.variants.map((variant) => variant.option2))).map((option2Value) => (
                  <TableCell key={option2Value}>{option2Value}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from(new Set(currentProduct.variants.map((variant) => variant.option1))).map((option1Value) => (
                <TableRow key={option1Value}>
                  <TableCell>
                    <Checkbox
                      onChange={() => {
                        const allVariantsForOption1 = currentProduct.variants.filter(
                          (variant) => variant.option1 === option1Value
                        );
                        const areAllSelected = allVariantsForOption1.every(
                          (variant) => selectedVariants[variant.id]
                        );
                        allVariantsForOption1.forEach((variant) => {
                          setSelectedVariants((prevSelected) => ({
                            ...prevSelected,
                            [variant.id]: !areAllSelected,
                          }));
                        });
                      }}
                      checked={currentProduct.variants
                        .filter((variant) => variant.option1 === option1Value)
                        .every((variant) => selectedVariants[variant.id])}
                    />
                    {option1Value}
                  </TableCell>
                  {Array.from(new Set(currentProduct.variants.map((variant) => variant.option2))).map((option2Value) => (
                    <TableCell key={option2Value}>
                      <Checkbox
                        onChange={() => handleCheckboxChange(
                          currentProduct.variants.find(
                            (variant) => variant.option1 === option1Value && variant.option2 === option2Value
                          )!.id
                        )}
                        checked={selectedVariants[
                          currentProduct.variants.find(
                            (variant) => variant.option1 === option1Value && variant.option2 === option2Value
                          )!.id
                        ] || false}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleBack} disabled={activeStep === 0}>
          <KeyboardArrowLeft />
          Back
        </Button>
        <Button onClick={handleNext} disabled={activeStep === selectedProducts.length - 1}>
          Next
          <KeyboardArrowRight />
        </Button>
        <Button onClick={handleCreateListings} color="primary" variant="contained">
          Create Listings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateListingsModal;
